import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';
import { isUncounted } from '../countEntry/lineState';
import ListGrid from '../../components/ListGrid';

/*
  The lines of a submitted sheet, and the gate on approving it.

  **This is the one thing that had to survive the Review Hub becoming a page**,
  in the build order's own words: **Approve is locked until you have read
  through every line.** It is a deliberate guard against approving a count
  nobody read, it has been in the app since the Hub was built, and it is not in
  the mockup the page was drawn from, because the mockup was drawn from a list
  of screens by somebody who did not know it existed.

  It unlocks when the list has been scrolled to its end, or straight away when
  the whole list fits without scrolling, which is the same rule the old row
  used.

  **The lines are read a page at a time**, because a request brings back about a
  thousand rows and says nothing about the rest, and a sheet of 486 lines that
  quietly stops is one an administrator approves believing they have seen it
  all.
*/
export default function CountReadThrough({ count, onReadToEnd }) {
  const [lines, setLines] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [atEnd, setAtEnd] = useState(false);
  const box = useRef(null);

  /*
    **A read that failed is not an empty sheet.** Until 18 September 2026 (the
    audit after the rebrand, issue 7) the error was thrown away and the lines
    were set to an empty list, which fits the box without scrolling, which
    counts as read to the end: a network hiccup unlocked Approve and Send back
    on a sheet nobody had seen. Now it says the lines could not be read, keeps
    both buttons locked, and offers to try again.
  */
  useEffect(() => {
    let live = true;
    setLines(null); setFailed(false); setAtEnd(false);
    (async () => {
      const { data, error } = await fetchAll(() => supabase
        .from('count_line_items')
        .select('id, item_number_raw, description_raw, vendor_raw, quantity, notes, not_in_catalog')
        .eq('inventory_count_id', count.id)
        .order('item_number_raw'));
      if (!live) return;
      if (error) { setFailed(true); return; }
      setLines(data || []);
    })();
    return () => { live = false; };
  }, [count.id, attempt]);

  // A short sheet has been read the moment it is on screen. Without this, a
  // sheet of four lines could never unlock its own Approve.
  useEffect(() => {
    if (lines === null) return;
    const el = box.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 4) reachedEnd();
  }, [lines]);

  function reachedEnd() {
    if (atEnd) return;
    setAtEnd(true);
    onReadToEnd?.();
  }

  function onScroll(e) {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) reachedEnd();
  }

  if (failed) {
    return (
      <div role="alert" style={{ padding: 20, fontSize: 13, color: 'var(--critical-ink)' }}>
        The lines on this sheet could not be read, so it cannot be approved or sent back yet.{' '}
        <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setAttempt(n => n + 1)}>Try again</button>
      </div>
    );
  }

  if (lines === null) {
    return <div style={{ padding: 20, color: 'var(--text-secondary)', fontSize: 13 }}>Reading the sheet...</div>;
  }

  const counted = lines.filter(l => !isUncounted(l)).length;
  const zeroes  = lines.filter(l => l.quantity === 0).length;
  const units   = lines.reduce((s, l) => s + (l.quantity || 0), 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div className="figure-strip" style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-soft)' }}>
        <Figure n={lines.length} label="Lines" dot="var(--text-primary)" />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        <Figure n={counted} label="Counted" dot="var(--success)" />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        <Figure n={zeroes} label="Counted as zero" dot="var(--critical)" />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        <Figure n={units} label="Units" dot="var(--text-primary)" />
      </div>

      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '14px 0 6px' }}>
        The lines, in item-number order
      </div>

      <div ref={box} onScroll={onScroll} className="hub-lines">
        <ListGrid
          stickyHead
          columns={[
            { label: 'Item',        width: '1.2fr' },
            { label: 'Description', width: '2.6fr' },
            { label: 'Vendor',      width: '1.4fr' },
            { label: 'Qty',         width: '80px', align: 'right' },
          ]}
          rows={lines}
          keyOf={l => l.id}
          empty="No lines on this sheet."
          rowClass={l => (isUncounted(l) ? 'state-blank' : 'state-counted')}
          cells={l => [
            <span style={{ fontWeight: 700, color: l.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>{l.item_number_raw}</span>,
            <span>
              {l.description_raw}
              {l.notes && <span style={{ display: 'block', fontSize: 11, color: 'var(--info-ink)', fontStyle: 'italic', marginTop: 2 }}>Note: {l.notes}</span>}
            </span>,
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{l.vendor_raw || '--'}</span>,
            <Qty line={l} />,
          ]}
          /* The narrow shape, for the same reason the read-only dialog needed
             one: six labelled lines per row inside a panel on a 390 phone is a
             description wrapping four times. */
          stack={l => (
            <span>
              <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: l.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>{l.item_number_raw}</span>
                <Qty line={l} />
              </span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-primary)', marginTop: 2 }}>{l.description_raw}</span>
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{l.vendor_raw || '--'}</span>
              {l.notes && <span style={{ display: 'block', fontSize: 11, color: 'var(--info-ink)', fontStyle: 'italic', marginTop: 2 }}>Note: {l.notes}</span>}
            </span>
          )}
        />
      </div>
    </div>
  );
}

// The number, or the fact that nobody has been to that shelf. Rule 9.
function Qty({ line }) {
  if (isUncounted(line)) {
    return <span style={{ color: 'var(--text-secondary)', fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase' }}>Not counted</span>;
  }
  return <span style={{ fontWeight: 700, color: line.quantity === 0 ? 'var(--critical-ink)' : 'var(--text-primary)' }}>{line.quantity}</span>;
}

function Figure({ n, label, dot }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, alignSelf: 'center', flexShrink: 0 }} />
      <b style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{typeof n === 'number' ? n.toLocaleString() : n}</b>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{label}</span>
    </span>
  );
}
